import { CSSProperties, MutableRefObject, useCallback, useMemo } from 'react'
import { Currency, CurrencyAmount, currencyEquals, ETHER, Token } from '@pancakeswap/sdk'
import { Text } from '@pancakeswap/uikit'
import styled, { keyframes } from 'styled-components'
import { FixedSizeList } from 'react-window'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { LightGreyCard } from 'components/Card'
import QuestionHelper from 'components/QuestionHelper'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { CopyButton } from 'components/CopyButton'
import { useCombinedActiveList } from '../../state/lists/hooks'
import { useCurrencyBalance } from '../../state/wallet/hooks'
import { useIsUserAddedToken } from '../../hooks/Tokens'
import Column from '../Layout/Column'
import { RowFixed, RowBetween } from '../Layout/Row'
import { CurrencyLogo } from '../Logo'
import CircleLoader from '../Loader/CircleLoader'
import { isTokenOnList } from '../../utils'
import ImportRow from './ImportRow'

import { isRobinhoodChain } from 'config/constants/chains'
function currencyKey(currency: Currency): string {
  return currency instanceof Token ? currency.address : currency === ETHER ? 'ETHER' : ''
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const StyledBalanceText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  max-width: 5rem;
  text-overflow: ellipsis;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

const FixedContentRow = styled.div`
  padding: 8px 16px;
  height: 72px;
  display: grid;
  grid-gap: 16px;
  align-items: center;
`

function Balance({ balance }: { balance: CurrencyAmount }) {
  return <StyledBalanceText title={balance.toExact()}>{balance.toSignificant(4)}</StyledBalanceText>
}

const TokenCard = styled.div<{ disabled: boolean; selected: boolean }>`
  margin: 4px 8px;
  padding: 16px;
  height: 72px;
  display: grid;
  grid-template-columns: auto minmax(auto, 1fr) auto auto;
  grid-gap: 12px;
  align-items: center;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
  border-radius: 16px;
  border: 2px solid transparent;
  background: ${({ theme, selected, disabled }) => {
    if (disabled || selected) return theme.colors.backgroundDisabled
    return `linear-gradient(135deg, ${theme.colors.backgroundAlt} 0%, ${theme.colors.backgroundAlt2} 100%)`
  }};
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  opacity: ${({ disabled, selected }) => (disabled || selected ? 0.6 : 1)};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -200px;
    width: 200px;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover:not([disabled]) {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 
      0 8px 25px rgba(0, 212, 170, 0.15),
      0 4px 12px rgba(0, 0, 0, 0.1);
    background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.backgroundAlt} 0%, ${theme.colors.primary}15 100%)`};

    &::before {
      left: calc(100% + 200px);
    }

    .token-logo {
      transform: scale(1.1);
    }

    .copy-button {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &:active:not([disabled]) {
    transform: translateY(0);
    transition: transform 0.1s;
  }
`

const TokenLogo = styled.div`
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
`

const TokenInfo = styled(Column)`
  min-width: 0;
  flex: 1;
`

const TokenSymbol = styled(Text)`
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`

const TokenName = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
  opacity: 0.8;
`

const CopyButtonWrapper = styled.div`
  opacity: 0;
  transform: translateX(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}20;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const BalanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 60px;
`

function CurrencyRow({
  currency,
  onSelect,
  isSelected,
  otherSelected,
  style,
}: {
  currency: Currency
  onSelect: () => void
  isSelected: boolean
  otherSelected: boolean
  style: CSSProperties
}) {
  const { account, chainId } = useActiveWeb3React()
  const { t } = useTranslation()
  const key = currencyKey(currency)

  const isNative = currency === ETHER
  const displaySymbol = isNative && isRobinhoodChain(chainId) ? 'ETH' : currency.symbol
  const displayName = isNative && isRobinhoodChain(chainId) ? 'Ether' : currency.name
  const selectedTokenList = useCombinedActiveList()
  const isOnSelectedList = isTokenOnList(selectedTokenList, currency)
  const customAdded = useIsUserAddedToken(currency)
  const balance = useCurrencyBalance(account ?? undefined, currency)

  // Get token address for copy functionality
  const tokenAddress = currency instanceof Token ? currency.address : isRobinhoodChain(chainId) ? 'ETH' : 'GRX'

  return (
    <div style={style}>
      <TokenCard
        className={`token-item-${key}`}
        onClick={() => (isSelected ? null : onSelect())}
        disabled={isSelected}
        selected={otherSelected}
      >
        <TokenLogo className="token-logo">
          <CurrencyLogo currency={currency} size="32px" />
        </TokenLogo>
        
        <TokenInfo>
          <TokenSymbol>{displaySymbol}</TokenSymbol>
          <TokenName ellipsis maxWidth="200px">
            {!isOnSelectedList && customAdded && `${t('Added by user')} • `}
            {displayName}
          </TokenName>
        </TokenInfo>

        <CopyButtonWrapper className="copy-button">
          <CopyButton
            text={tokenAddress}
            tooltipMessage={t('Copied!')}
            tooltipTop={-40}
            tooltipRight={0}
            width="16px"
            buttonColor="textSubtle"
          />
        </CopyButtonWrapper>

        <BalanceWrapper>
          {balance ? <Balance balance={balance} /> : account ? <CircleLoader /> : null}
        </BalanceWrapper>
      </TokenCard>
    </div>
  )
}

export default function CurrencyList({
  height,
  currencies,
  inactiveCurrencies,
  selectedCurrency,
  onCurrencySelect,
  otherCurrency,
  fixedListRef,
  showBNB,
  showImportView,
  setImportToken,
  breakIndex,
}: {
  height: number
  currencies: Currency[]
  inactiveCurrencies: Currency[]
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherCurrency?: Currency | null
  fixedListRef?: MutableRefObject<FixedSizeList | undefined>
  showBNB: boolean
  showImportView: () => void
  setImportToken: (token: Token) => void
  breakIndex: number | undefined
}) {
  const itemData: (Currency | undefined)[] = useMemo(() => {
    let formatted: (Currency | undefined)[] = showBNB
      ? [Currency.ETHER, ...currencies, ...inactiveCurrencies]
      : [...currencies, ...inactiveCurrencies]
    if (breakIndex !== undefined) {
      formatted = [...formatted.slice(0, breakIndex), undefined, ...formatted.slice(breakIndex, formatted.length)]
    }
    return formatted
  }, [breakIndex, currencies, inactiveCurrencies, showBNB])

  const { chainId } = useActiveWeb3React()

  const { t } = useTranslation()

  const Row = useCallback(
    ({ data, index, style }) => {
      const currency: Currency = data[index]
      const isSelected = Boolean(selectedCurrency && currencyEquals(selectedCurrency, currency))
      const otherSelected = Boolean(otherCurrency && currencyEquals(otherCurrency, currency))
      const handleSelect = () => onCurrencySelect(currency)

      const token = wrappedCurrency(currency, chainId)

      const showImport = index > currencies.length

      if (index === breakIndex || !data) {
        return (
          <FixedContentRow style={style}>
            <LightGreyCard padding="8px 12px" borderRadius="8px">
              <RowBetween>
                <Text small>{t('Expanded results from inactive Token Lists')}</Text>
                <QuestionHelper
                  text={t(
                    "Tokens from inactive lists. Import specific tokens below or click 'Manage' to activate more lists.",
                  )}
                  ml="4px"
                />
              </RowBetween>
            </LightGreyCard>
          </FixedContentRow>
        )
      }

      if (showImport && token) {
        return (
          <ImportRow style={style} token={token} showImportView={showImportView} setImportToken={setImportToken} dim />
        )
      }
      return (
        <CurrencyRow
          style={style}
          currency={currency}
          isSelected={isSelected}
          onSelect={handleSelect}
          otherSelected={otherSelected}
        />
      )
    },
    [
      selectedCurrency,
      otherCurrency,
      chainId,
      currencies.length,
      breakIndex,
      onCurrencySelect,
      t,
      showImportView,
      setImportToken,
    ],
  )

  const itemKey = useCallback((index: number, data: any) => currencyKey(data[index]), [])

  return (
    <FixedSizeList
      height={height}
      ref={fixedListRef as any}
      width="100%"
      itemData={itemData}
      itemCount={itemData.length}
      itemSize={80}
      itemKey={itemKey}
    >
      {Row}
    </FixedSizeList>
  )
}
